<template>
  <div
    class="c-list--metrics__item"
    :class="{
      'is-checked': isChecked && !isDisabledCv,
      'is-loading': isLoading
    }"
  >
    <Loading v-if="isLoading" />
    <div v-if="isDisabledCv" class="c-list--metrics__label">
      <div class="c-list--metrics-summary">
        <Icon :variant="itemData.icon" :size="size[itemData.icon]" radius />
        <div class="c-list--metrics-summary__data">
          <p class="c-list--metrics-summary__category">{{ itemData.label }}</p>
          <p class="c-list--metrics-summary__text">
            データがありません。
            <Tooltip>
              <template v-if="gaType === 'ga4'">
                Google Analyticsでコンバージョンイベントを設定してください。<a
                  href="https://support.google.com/analytics/answer/12966437?hl=ja"
                  target="_blank"
                  >設定の仕方</a
                >
              </template>
              <template v-else>
                Google Analyticsコンバージョンの目標設定をしてください。<a
                  href="https://support.google.com/analytics/answer/1032415"
                  target="_blank"
                  >目標設定の仕方</a
                >
              </template>
            </Tooltip>
          </p>
        </div>
      </div>
    </div>
    <label v-else class="c-list--metrics__label">
      <input
        type="checkbox"
        :value="id"
        :checked="isChecked"
        class="c-list--metrics__input"
        @change="change"
      />
      <div class="c-list--metrics-summary">
        <Icon
          :variant="itemData.icon"
          :size="size[itemData.icon]"
          radius
          :class="'c-color-bg--' + itemData.color"
        />
        <div class="c-list--metrics-summary__data">
          <p class="c-list--metrics-summary__category">{{ itemData.label }}</p>
          <p>
            <span class="c-list--metrics-summary__count">{{
              valueText.value
            }}</span>
            <span class="c-list--metrics-summary__unit">{{
              itemData.unit
            }}</span>
          </p>
        </div>
      </div>
      <ul class="c-list--metrics-comparison">
        <li class="c-list--metrics-comparison__item">
          前月比
          <template v-if="periodLastMonth">
            <small>
              ({{ periodLastMonth[0] | dateText('YYYY.MM.DD') }}-{{
                periodLastMonth[1] | dateText('YYYY.MM.DD')
              }})
            </small>
            {{ valueText.lastMonth ? valueText.lastMonth + '%' : '-' }}
            <Icon2
              v-if="comparisonLastMonth"
              :variant="comparisonLastMonth"
              size="10"
            />
          </template>
          <template v-else>
            -
          </template>
        </li>
        <li class="c-list--metrics-comparison__item">
          前年比
          <template v-if="periodLastYear">
            <small>
              ({{ periodLastYear[0] | dateText('YYYY.MM.DD') }}-{{
                periodLastYear[1] | dateText('YYYY.MM.DD')
              }})
            </small>
            {{ valueText.lastYear ? valueText.lastYear + '%' : '-' }}
            <Icon2
              v-if="comparisonLastYear"
              :variant="comparisonLastYear"
              size="10"
            />
          </template>
          <template v-else>
            -
          </template>
        </li>
      </ul>
    </label>
  </div>
</template>

<script>
import Loading from '@/components/Loading';
import Icon from '@/components/Icon';
import Icon2 from '@/components/Icon2';
import Tooltip from '@/components/Tooltip';

export default {
  props: {
    checked: {
      type: Array,
      required: true
    },
    gaType: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    item: {
      type: Object,
      required: false
    },
    itemData: {
      type: Object,
      required: true
    },
    periodLastMonth: {
      type: Array,
      required: false
    },
    periodLastYear: {
      type: Array,
      required: false
    },
    isLoading: {
      type: Boolean,
      required: false
    }
  },
  components: {
    Loading,
    Icon,
    Icon2,
    Tooltip
  },
  data() {
    return {
      size: {
        bounce: 20,
        conversion: 18,
        imp: 18,
        mouse: 18,
        new: 16,
        percent: 16,
        rank_change: 18,
        reload: 18,
        session: 18,
        time: 18,
        user: 18
      }
    };
  },
  computed: {
    comparisonLastMonth() {
      return this.getComparison(this.valueText.lastMonth);
    },
    comparisonLastYear() {
      return this.getComparison(this.valueText.lastYear);
    },
    valueText() {
      // APIからString型で返ってくるものがあるので、数値に変換する
      let lastMonth = this.item ? Number(this.item.lastMonth) : null;
      let lastYear = this.item ? Number(this.item.lastYear) : null;
      let value = this.item ? Number(this.item.value) : 0;

      if (lastMonth > 0) {
        lastMonth = Math.round((value / lastMonth) * 100);
      }
      if (lastYear > 0) {
        lastYear = Math.round((value / lastYear) * 100);
      }
      if (this.itemData.type === 'number') {
        value = this.$options.filters.separateNumber(
          this.$options.filters.roundFloatingPointNumber(value, 2)
        );
      } else if (
        this.itemData.type === 'rank' ||
        this.itemData.type === 'position'
      ) {
        value = this.$options.filters.roundFloatingPointNumber(value, 2);
      } else if (this.itemData.type === 'percentage') {
        value = this.$options.filters.roundFloatingPointNumber(value * 100, 2);
      } else if (this.itemData.type === 'time') {
        value = this.$options.filters.secondsToClock(value);
      }
      return { lastMonth, lastYear, value };
    },
    isChecked() {
      return this.checked.indexOf(this.id) !== -1;
    },
    isDisabledCv() {
      return this.id === 'conversion' && this.item && this.item.value === null;
    }
  },
  methods: {
    change(e) {
      let checked = e.target.checked;
      let index = this.checked.indexOf(this.id);
      let array = this.checked.slice();
      if (checked && index === -1) {
        array.push(this.id);
      } else if (!checked && index !== -1) {
        array.splice(index, 1);
      }
      this.$emit('change', array);
    },
    getComparison(value) {
      if (value) {
        if (value === 100) {
          return 'same';
        } else if (value >= 100) {
          return 'up';
        } else if (value >= 1) {
          return 'down';
        }
      }
      return '';
    }
  }
};
</script>
